import { 
  Button, CircularProgress, Container, Stack, TextField, Typography, Tabs, Tab, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, 
  IconButton, Tooltip 
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import Page from '../components/Page';
import MatterList from '../sections/matter/MatterList';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DeleteIcon from '@mui/icons-material/Delete';

export default function RecycleBinPage() {
  const [matters, setMatters] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  
  // Pagination logic
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 16;

  const isAdmin = user?.role === 'admin';

  // 🎯 Reset page when tab or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [currentTab, searchTerm]);

 useEffect(() => {
  const fetchUserAndData = async () => {
    setLoading(true);
    try {
      // 1. Fetch User first to check permissions
      const userRes = await axios.get('https://demo.insollex.com/api/user', { withCredentials: true });
      setUser(userRes.data);

      if (userRes.data?.role === 'admin') {
        if (currentTab === 0) {
          // 🎯 Added { withCredentials: true } to ensure session carries over
          const mattersRes = await axios.get('https://demo.insollex.com/api/legal-matter/recycle-bin', { withCredentials: true });
          
          // 🎯 Match the backend response: { matters: [...] }
          // We prioritize mattersRes.data.matters because your backend uses: res.json({ matters })
          const allMatters = mattersRes.data?.matters || (Array.isArray(mattersRes.data) ? mattersRes.data : []);
          setMatters(allMatters);
        } else {
          const docsRes = await axios.get('https://demo.insollex.com/api/documents/recycle-bin', { withCredentials: true });
          
          // Match the backend response for documents
          const allDocs = docsRes.data?.files || (Array.isArray(docsRes.data) ? docsRes.data : []);
          setDocuments(allDocs);
        }
      }
    } catch (err) {
      // 🎯 Improved logging to see if it's a 404 (route order) or 500 (DB error)
      console.error('Error fetching data:', err.response?.data || err.message);
      
      // Don't show an error if the user just isn't logged in yet
      if (err.response?.status !== 401) {
        enqueueSnackbar('Failed to fetch Recycle Bin data', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  fetchUserAndData();
}, [enqueueSnackbar, currentTab]); // 🎯 Removed 'user' from dependency to avoid infinite loops

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleRestoreMatter = async (id) => {
    try {
      await axios.post(`https://demo.insollex.com/api/legal-matter/${id}/restore`, {}, { withCredentials: true });
      setMatters(prevMatters => prevMatters.filter(m => m.id !== id));
      enqueueSnackbar('Matter restored successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error restoring matter:', error);
      enqueueSnackbar('Failed to restore matter', { variant: 'error' });
    }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this matter? This cannot be undone.")) return;
    try {
      await axios.delete(`https://demo.insollex.com/api/legal-matter/${id}/permanent`, { withCredentials: true });
      setMatters(prevMatters => prevMatters.filter(m => m.id !== id));
      enqueueSnackbar('Matter permanently deleted', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting matter:', error);
      enqueueSnackbar('Failed to delete matter', { variant: 'error' });
    }
  };

  const handleRestoreDocument = async (doc) => {
    try {
      if (!doc.legalMatter || !doc.legalMatter.id) {
          enqueueSnackbar('Cannot restore document: Associated matter not found.', { variant: 'error' });
          return;
      }
      await axios.post(`https://demo.insollex.com/api/legal-matter/${doc.legalMatter.id}/documents/${doc.filename}/restore`, {}, { withCredentials: true });
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      enqueueSnackbar('Document restored successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error restoring document:', error);
      enqueueSnackbar('Failed to restore document', { variant: 'error' });
    }
  };

  const handlePermanentDeleteDocument = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this document? This cannot be undone.")) return;
    try {
      await axios.delete(`https://demo.insollex.com/api/documents/${id}/permanent`, { withCredentials: true });
      setDocuments(prev => prev.filter(d => d.id !== id));
      enqueueSnackbar('Document permanently deleted', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting document:', error);
      enqueueSnackbar('Failed to delete document', { variant: 'error' });
    }
  };

  // 🎯 Updated Filter: Removed the strict matter.status === 'Deleted' check 
  // because the endpoint already returns only deleted items.
  const filteredMatters = (matters || [])
    .filter((matter) => {
      if (searchTerm.trim() === '') return true;
      const term = searchTerm.toLowerCase();
      return (
        matter?.matterName?.toLowerCase().includes(term) ||
        matter?.estateNo?.toLowerCase?.().includes(term) ||
        matter?.client?.toLowerCase?.().includes(term)
      );
    });

  const filteredDocuments = (documents || [])
    .filter((doc) => {
      if (searchTerm.trim() === '') return true;
      const term = searchTerm.toLowerCase();
      return (
        doc?.filename?.toLowerCase().includes(term) ||
        doc?.legalMatter?.matterName?.toLowerCase().includes(term)
      );
    });

  const paginatedMatters = filteredMatters.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const totalPages = Math.ceil(filteredMatters.length / pageSize) || 1;

  return (
    <Page title="Recycle Bin">
      <Container maxWidth={false}>
        <Typography variant="h4" sx={{ mb: 5 }}>
          Recycle Bin
        </Typography>

        <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Matters" />
          <Tab label="Documents" />
        </Tabs>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <TextField
            label={currentTab === 0 ? "Search Deleted Matters" : "Search Deleted Documents"}
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: '100%', maxWidth: '600px' }}
          />
        </Stack>

        {loading ? (
          <CircularProgress sx={{ display: 'block', margin: '40px auto' }} />
        ) : (
          <>
            {currentTab === 0 && (
              <>
                {filteredMatters.length > 0 ? (
                  <MatterList
                    matters={paginatedMatters}
                    isAdmin={isAdmin}
                    isDeletedView={true}
                    onRestoreMatter={handleRestoreMatter}
                    onDeleteMatter={handlePermanentDelete}
                    setMatters={setMatters}
                    statusTooltips={{}}
                  />
                ) : (
                  <Paper sx={{ p: 5, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">No deleted matters found.</Typography>
                  </Paper>
                )}
              </>
            )}

            {currentTab === 1 && (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Filename</TableCell>
                      <TableCell>Matter Name</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>{doc.filename}</TableCell>
                        <TableCell>{doc.legalMatter?.matterName || 'Unknown Matter'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Restore">
                            <IconButton onClick={() => handleRestoreDocument(doc)} color="success">
                              <RestoreFromTrashIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Permanently Delete">
                            <IconButton onClick={() => handlePermanentDeleteDocument(doc.id)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredDocuments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">No deleted documents found.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* Pagination Controls - Only show for Matters */}
        {currentTab === 0 && filteredMatters.length > pageSize && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mt: 3, gap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>{'<<'}</Button>
            <Button size="small" variant="outlined" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>{'<'}</Button>
            <Typography sx={{ px: 2 }}>Page {currentPage} of {totalPages}</Typography>
            <Button size="small" variant="outlined" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>{'>'}</Button>
            <Button size="small" variant="outlined" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>{'>>'}</Button>
          </Stack>
        )}
      </Container>
    </Page>
  );
}